<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import { OnClickOutside } from '@vueuse/components';

import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  agent: {
    type: Object,
    required: true,
  },
  isToggling: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['edit', 'toggleActive', 'duplicate', 'delete']);

const { t } = useI18n();
const [showActionsMenu, toggleActionsMenu] = useToggle(false);

const isActive = computed(
  () => props.agent.status === 'published' && props.agent.is_active
);

const statusBadge = computed(() => {
  if (isActive.value) {
    return {
      label: t('AI_AGENTS.STATUS.active'),
      className: 'bg-n-teal-3 text-n-teal-11',
    };
  }

  const statusClassMap = {
    draft: 'bg-n-amber-3 text-n-amber-11',
    published: 'bg-n-slate-3 text-n-slate-11',
    archived: 'bg-n-slate-3 text-n-slate-11',
  };

  return {
    label: t(`AI_AGENTS.STATUS.${props.agent.status}`),
    className:
      statusClassMap[props.agent.status] || 'bg-n-alpha-2 text-n-slate-11',
  };
});

const menuSections = computed(() => [
  {
    items: [
      {
        action: 'duplicate',
        value: 'duplicate',
        label: t('AI_AGENTS.CARD.DUPLICATE'),
        icon: 'i-lucide-copy',
      },
    ],
  },
  {
    items: [
      {
        action: 'delete',
        value: 'delete',
        label: t('AI_AGENTS.ACTIONS.DELETE'),
        icon: 'i-lucide-trash',
      },
    ],
  },
]);

const handleMenuAction = ({ action }) => {
  toggleActionsMenu(false);

  if (action === 'duplicate') {
    emit('duplicate', props.agent);
    return;
  }

  if (action === 'delete') {
    emit('delete', props.agent);
  }
};

const handleToggleActive = isEnabled => {
  emit('toggleActive', { agent: props.agent, isActive: isEnabled });
};
</script>

<template>
  <article
    class="flex min-h-[220px] flex-col rounded-xl border border-n-weak bg-n-background p-4"
  >
    <span
      class="inline-flex w-fit items-center rounded-full px-2 py-0.5 text-xs font-medium"
      :class="statusBadge.className"
    >
      {{ statusBadge.label }}
    </span>

    <div
      class="mt-3 flex size-10 shrink-0 items-center justify-center rounded-full bg-n-brand/10 text-lg"
    >
      {{ agent.emoji || '🤖' }}
    </div>

    <h3 class="m-0 mt-3 text-base font-semibold text-n-slate-12">
      {{ agent.name }}
    </h3>

    <p
      v-if="agent.description"
      class="m-0 mt-1 line-clamp-3 flex-1 text-sm leading-snug text-n-slate-11"
    >
      {{ agent.description }}
    </p>
    <p v-else class="m-0 mt-1 flex-1 text-sm text-n-slate-11">
      {{ t('AI_AGENTS.CARD.NO_DESCRIPTION') }}
    </p>

    <div class="mt-4 flex items-center justify-between gap-2">
      <div class="flex items-center gap-2">
        <Button
          :label="t('AI_AGENTS.ACTIONS.EDIT')"
          icon="i-lucide-pencil"
          outline
          slate
          sm
          class="!bg-n-background"
          @click="emit('edit', agent)"
        />
        <div class="relative">
          <OnClickOutside @trigger="toggleActionsMenu(false)">
            <Button
              icon="i-lucide-ellipsis"
              outline
              slate
              sm
              class="!bg-n-background"
              @click="toggleActionsMenu()"
            />
            <DropdownMenu
              v-if="showActionsMenu"
              :menu-sections="menuSections"
              class="absolute top-full z-20 mt-1 min-w-[11rem] ltr:left-0 rtl:right-0"
              @action="handleMenuAction"
            />
          </OnClickOutside>
        </div>
      </div>

      <Switch
        :model-value="isActive"
        size="md"
        :disabled="isToggling"
        @change="handleToggleActive"
      />
    </div>
  </article>
</template>
